import { PSV_REVIEW_POLL_INTERVAL_MS } from "./psv-review/review-query";
import { PsvProviderStatus } from "@api/shared-runtime/psv-review";
import { useCallback, useEffect, useState } from "react";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { FileSpreadsheet, Mail, Settings, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTRPC } from "@/trpc";
import { InviteProviderDialog } from "@/routes/_org/org/bu/$organizationBusinessUnitId/providers/invitations/_components/invite-provider-dialog";
import { ProviderCsvImportDialog } from "@/routes/_org/org/bu/$organizationBusinessUnitId/providers/invitations/_components/provider-csv-import-dialog";
import { WorkspaceFilters } from "./workspace-filters";
import { WorkspaceTable } from "./workspace-table";
import { WorkspacePagination } from "./workspace-pagination";
import { WorkspaceInvitations } from "./workspace-invitations";
import { DensityToggle } from "./workspace-grid/density-toggle";
import { PsvStatusTabs } from "./workspace-grid/psv-status-tabs";
import { ReviewBanner } from "./workspace-grid/review-banner";
import {
  useWorkspaceDensity,
  WorkspaceDensity,
} from "./workspace-grid/use-workspace-density";
import { ProviderStartVerification } from "./provider-start-verification";
import { saveLastWorkspaceSearch } from "./workspace-last-search";
import { useOpenProviderPage } from "./use-provider-page-navigation";
import { useWorkspaceRefresh } from "./use-workspace-refresh";
import type { WorkspaceProvider } from "./workspace-types";
import {
  changeWorkspaceFilters,
  lastWorkspaceOffset,
  normalizeWorkspaceFilters,
  type WorkspaceSearch,
} from "./workspace-search";

export function ProviderWorkspace({
  organizationBusinessUnitId,
  search,
  onNavigate,
}: {
  organizationBusinessUnitId: string;
  search: WorkspaceSearch;
  onNavigate: (search: WorkspaceSearch) => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const openProviderPage = useOpenProviderPage(organizationBusinessUnitId);
  const { density, changeDensity } = useWorkspaceDensity();
  const [startingProvider, setStartingProvider] =
    useState<WorkspaceProvider | null>(null);
  const [addedProviders, setAddedProviders] = useState(false);
  const onProvidersAdded = async (savedBusinessUnitId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: trpc.providerWorkspace.list.queryKey({
          organizationBusinessUnitId: savedBusinessUnitId,
        }),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.providerWorkspace.listInvitations.queryKey({
          organizationBusinessUnitId: savedBusinessUnitId,
        }),
      }),
    ]);
    setAddedProviders(true);
  };
  useWorkspaceRefresh(organizationBusinessUnitId);
  const filters = normalizeWorkspaceFilters(search);
  const query = useQuery(
    trpc.providerWorkspace.list.queryOptions(
      {
        organizationBusinessUnitId,
        ...filters,
      },
      {
        // The workspace is keyed by business unit. Keep its permission-gated
        // controls stable during filter changes, but hide the previous rows.
        placeholderData: keepPreviousData,
        refetchInterval: (query) => {
          const data = query.state.data;
          if (
            !data?.primarySourceVerificationEnabled ||
            !data.capabilities.canViewCredentials
          ) {
            return false;
          }
          // Worker completions and other reviewers' decisions also change rows.
          return PSV_REVIEW_POLL_INTERVAL_MS;
        },
      },
    ),
  );
  const pools = useQuery({
    ...trpc.providerWorkspace.poolOptions.queryOptions({
      organizationBusinessUnitId,
    }),
    enabled: !!query.data?.capabilities.canViewPools,
  });
  const [addOpen, setAddOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [invitationsOpen, setInvitationsOpen] = useState(false);
  const updateFilters = useCallback(
    (changes: Partial<WorkspaceSearch>) =>
      onNavigate(changeWorkspaceFilters({ previous: search, changes })),
    [search, onNavigate],
  );
  useEffect(() => {
    if (
      !query.data ||
      query.isFetching ||
      filters.offset <= lastWorkspaceOffset(query.data.total)
    ) {
      return;
    }
    onNavigate({
      ...search,
      offset: lastWorkspaceOffset(query.data.total) || undefined,
    });
  }, [query.data, query.isFetching, filters.offset, search, onNavigate]);
  useEffect(() => {
    saveLastWorkspaceSearch({ organizationBusinessUnitId, search });
  }, [organizationBusinessUnitId, search]);
  const showVerification =
    !!query.data?.primarySourceVerificationEnabled &&
    !!query.data.capabilities.canViewCredentials;
  const openProvider = (provider: WorkspaceProvider) =>
    void openProviderPage({ affiliationId: provider.affiliationId });
  const canManage =
    !query.isError && query.data?.capabilities.canManageAffiliations;
  // Monthly monitoring is the only credential setting today, so the Settings
  // entry follows the same gate as the verification UI.
  const showSettings = showVerification && !query.isError;
  const actionButtons = (showSettings || canManage) && (
    <div className="flex flex-wrap gap-2 pb-2">
      {showSettings && (
        <Button asChild variant="outline">
          <Link
            to="/org/bu/$organizationBusinessUnitId/providers/credentials/settings"
            params={{ organizationBusinessUnitId }}
          >
            <Settings className="mr-2 size-4" aria-hidden="true" />
            Settings
          </Link>
        </Button>
      )}
      {canManage && (
        <>
          <Button variant="outline" onClick={() => setInvitationsOpen(true)}>
            <Mail className="mr-2 size-4" />
            Invitations
          </Button>
          <Button variant="outline" onClick={() => setCsvOpen(true)}>
            <FileSpreadsheet className="mr-2 size-4" />
            Import CSV
          </Button>
          <Button onClick={() => setAddOpen(true)}>
            <UserPlus className="mr-2 size-4" />
            Add provider
          </Button>
        </>
      )}
    </div>
  );
  return (
    <div className="space-y-6 p-4 md:p-6">
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Credentials / Verification
        </p>
        <h1 className="text-2xl font-semibold tracking-tight">
          Provider credentials
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Providers, credentials and invitations for this business unit.
        </p>
      </header>
      {addedProviders && filters.employment === "hired" && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm"
        >
          <p>
            Providers added. Select All providers to include candidates hidden
            by the Hired filter.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => updateFilters({ employment: "all" })}
          >
            Show all providers
          </Button>
        </div>
      )}
      {showVerification && !query.isError && (
        <ReviewBanner
          key={organizationBusinessUnitId}
          organizationBusinessUnitId={organizationBusinessUnitId}
          // The banner counts every provider awaiting review, hired or not,
          // so the shortcut drops filters that would hide some of them.
          onViewProviders={() =>
            onNavigate({
              employment: "all",
              psv: PsvProviderStatus.NeedsReview,
            })
          }
        />
      )}
      <div className="flex flex-wrap items-end justify-between gap-x-4 border-b">
        {showVerification ? (
          <PsvStatusTabs
            value={search.psv}
            onChange={(psv) => updateFilters({ psv })}
          />
        ) : (
          <span />
        )}
        {actionButtons}
      </div>
      <WorkspaceFilters
        search={search}
        pools={pools.data ?? []}
        canViewPools={query.data?.capabilities.canViewPools ?? false}
        canViewCredentials={
          query.data?.capabilities.canViewCredentials ?? false
        }
        onChange={updateFilters}
      />
      {pools.isError && (
        <p role="alert" className="text-sm text-destructive">
          Couldn’t load provider pools.{" "}
          <button className="underline" onClick={() => void pools.refetch()}>
            Try again
          </button>
        </p>
      )}
      {query.isError && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 p-4"
        >
          <p>{query.error.message}</p>
          <Button
            className="mt-2"
            variant="outline"
            onClick={() => void query.refetch()}
          >
            Try again
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {query.data && !query.isError && !query.isPlaceholderData && (
            <>
              <span className="tabular-nums">{query.data.total}</span>{" "}
              {query.data.total === 1 ? "provider" : "providers"}
            </>
          )}
        </p>
        <DensityToggle density={density} onChange={changeDensity} />
      </div>
      <div
        className="overflow-hidden rounded-lg border bg-background"
        aria-busy={query.isFetching}
      >
        {(query.isPending || query.isPlaceholderData) && (
          <p role="status" className="p-12 text-center text-muted-foreground">
            Loading providers…
          </p>
        )}
        {query.data && !query.isError && !query.isPlaceholderData && (
          <>
            <WorkspaceTable
              providers={query.data.rows}
              showVerification={showVerification}
              compact={density === WorkspaceDensity.Compact}
              expiration={search.expiration}
              onOpen={openProvider}
              onRunVerification={setStartingProvider}
            />
            {query.data.rows.length === 0 && (
              <div className="space-y-2 p-10 text-center">
                {search.expiration ? (
                  <>
                    <p className="font-medium">
                      No providers match this expiration filter.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Try another expiration option or adjust your other
                      filters.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => updateFilters({ expiration: undefined })}
                    >
                      Clear expiration filter
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="font-medium">
                      No providers match these filters.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Select All providers to see your full active roster.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() =>
                        updateFilters({
                          employment: "all",
                          q: undefined,
                          pools: undefined,
                          specialties: undefined,
                          expiration: undefined,
                          psv: undefined,
                        })
                      }
                    >
                      Show all providers
                    </Button>
                  </>
                )}
              </div>
            )}
            <WorkspacePagination
              offset={filters.offset}
              total={query.data.total}
              pending={query.isFetching}
              onChange={(offset) =>
                onNavigate({ ...search, offset: offset || undefined })
              }
            />
          </>
        )}
      </div>
      {startingProvider && (
        <ProviderStartVerification
          key={startingProvider.affiliationId}
          provider={startingProvider}
          organizationBusinessUnitId={organizationBusinessUnitId}
          onClose={() => setStartingProvider(null)}
          onRestoreFocus={() => {}}
        />
      )}
      {addOpen && (
        <InviteProviderDialog
          variant="workspace"
          open
          onSaved={onProvidersAdded}
          onOpenChange={setAddOpen}
          onViewProvider={(affiliationId) => {
            setAddOpen(false);
            void openProviderPage({ affiliationId });
          }}
        />
      )}
      {csvOpen && (
        <ProviderCsvImportDialog
          open
          onSaved={onProvidersAdded}
          onOpenChange={setCsvOpen}
        />
      )}
      {invitationsOpen && (
        <WorkspaceInvitations
          organizationBusinessUnitId={organizationBusinessUnitId}
          onClose={() => setInvitationsOpen(false)}
        />
      )}
    </div>
  );
}
